import { useEffect, useRef } from "react";

import { useRect } from "@aircall/hooks";
import { InformationOutlined } from "@aircall/icons";
import { Flex, Icon, Typography, useAudio } from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { PlaybackControls } from "../PlaybackControls/PlaybackControls";
import {
  getRichAudioBarHeight,
  getSimpleAudioBarHeight,
} from "../PlayerAudioBar/PlayerAudioBar.styled";
import { PlayerSpeedControls } from "../PlayerSpeedControls";
import { PlayerTimelineActionsPicker } from "../PlayerTimelineActionsPicker";
import { PlayerTimelinePicker } from "../PlayerTimelinePicker";

import { PlayerProps } from "./Player.decl";
import { useAudioUrlExpired } from "./Player.hooks";
import * as S from "./Player.styled";
import { AssetColorsMapper } from "./Player.utils";
import { PlayerFullScreenToggle } from "./PlayerFullScreenToggle";

import { getAssetType } from "@/states/call/call.utility";
import { usePlayerHeight } from "@/states/playerHeight/playerHeight";
import { useBaseStore } from "@/store/store";

import { useCall } from "@/hooks/useCall/useCall";
import { useTranscriptionRule } from "@/hooks/useTranscriptionRules/useTranscriptionRules";

export function Player({ mobile }: PlayerProps) {
  const { call } = useCall();
  const mode = useBaseStore((store) => store.mode);
  const audio = useBaseStore((store) => store.audio);
  const { isRealTimeTranscription } = useTranscriptionRule();
  const { t } = useTranslation();

  const setHeight = usePlayerHeight((state) => state.setPlayerHeight);

  const elementRef = useRef<HTMLDivElement>(null);
  const containerDim = useRect(elementRef.current);

  useEffect(() => {
    containerDim?.height && setHeight(containerDim?.height);
  }, [containerDim?.height]);

  const { duration } = useAudio();
  const { isLoading } = useAudioUrlExpired();

  if (!call) {
    return null;
  }

  const assetType = getAssetType(call.type);
  const color = AssetColorsMapper[assetType];

  const disabled = !duration;

  if (isRealTimeTranscription && !audio?.url) {
    return (
      <S.Container data-test="player-no-recording">
        <Flex gap="xxxs" alignItems="center" justifyContent="center">
          <Icon
            component={InformationOutlined}
            size={12}
            color="text-secondary"
          />
          <Typography variant="bodyRegularS" color="text-secondary">
            {t("no_audio_recording")}
          </Typography>
        </Flex>
      </S.Container>
    );
  }

  return (
    <S.Container
      disabled={disabled}
      data-test="player-container"
      ref={elementRef}
    >
      <S.AnimatedContainer
        // we have to calculate the exact height of the simple nad rich audioBar to make the transition work
        h={
          mode !== "simple" && call.speakers?.size
            ? getRichAudioBarHeight(call.speakers?.size)
            : getSimpleAudioBarHeight()
        }
      >
        <PlayerTimelinePicker color={color} speakers={call.speakers} />
      </S.AnimatedContainer>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap="xxs" alignItems="center">
          <PlaybackControls
            color={color}
            loading={isLoading}
            disabled={disabled}
          />
          <PlayerSpeedControls disabled={disabled} />
        </Flex>
        <Flex gap="xxs" alignItems="center">
          {call.transcriptStatus === "available" &&
            !isRealTimeTranscription && <PlayerTimelineActionsPicker />}
          {!mobile && <PlayerFullScreenToggle />}
        </Flex>
      </Flex>
    </S.Container>
  );
}
